import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../components/common/LanguageSwitcher';
import { ChevronRight, Eye, EyeOff, ArrowLeft, Trophy, Award, Activity, Target } from 'lucide-react';

/**
 * LoginPage — Performance Club dark theme.
 *
 * Visual language matches /, /free, /about, /guide, /features, /contact.
 * Left: hero with kicker + title + 4-tile feature grid. Right: login form
 * with dark inputs + lime submit.
 */
export const LoginPage = () => {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      // Error handled in context
    } finally {
      setLoading(false);
    }
  };

  // Four small feature tiles rendered on the left side of the split.
  const features = [
    {
      icon: Trophy,
      title: t('loginPage.features.gamifiedTraining.title', { defaultValue: 'Gamified training' }),
      desc: t('loginPage.features.gamifiedTraining.desc', { defaultValue: 'Points, badges, and rewards that show up.' }),
    },
    {
      icon: Activity,
      title: t('loginPage.features.trackProgress.title', { defaultValue: 'Track progress' }),
      desc: t('loginPage.features.trackProgress.desc', { defaultValue: 'Sessions, streaks, recovery loops in one surface.' }),
    },
    {
      icon: Target,
      title: t('loginPage.features.teamChallenges.title', { defaultValue: 'Team challenges' }),
      desc: t('loginPage.features.teamChallenges.desc', { defaultValue: 'Compete with friends. Visible momentum.' }),
    },
    {
      icon: Award,
      title: t('loginPage.features.leaderboards.title', { defaultValue: 'Live leaderboards' }),
      desc: t('loginPage.features.leaderboards.desc', { defaultValue: 'Climb the rankings. 24/7 signals.' }),
    },
  ];

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side — Performance Club hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden p-12 flex-col justify-between border-r border-[var(--color-border)]" style={{ background: 'linear-gradient(135deg, #060b0a 0%, #0d1512 48%, #09100e 100%)' }}>
        <div className="hero-media-plane" />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-primary-200/30 bg-primary-200/10 font-display text-lg uppercase text-primary-100">GG</span>
            <div>
              <p className="font-display text-lg uppercase tracking-[0.18em] text-[var(--color-text)]">GymGamify</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)]">Performance Club OS</p>
            </div>
          </Link>
        </div>

        <div className="relative z-10 space-y-6">
          <span className="hero-kicker">{t('loginPage.hero.kicker', { defaultValue: 'Membership access' })}</span>
          <h2 className="editorial-title text-[var(--color-text)]">
            {t('loginPage.hero.title', { defaultValue: 'Welcome back to the floor.' })}
          </h2>
          <p className="max-w-md text-lg leading-8 text-[var(--color-text-muted)]">
            {t('loginPage.hero.subtitle', {
              defaultValue:
                'Same engine, same membership, same gym. Free forever, for every gym.',
            })}
          </p>

          <div className="grid grid-cols-2 gap-3 mt-8 max-w-md">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.title} className="editorial-panel flex flex-col gap-2 p-4">
                  <Icon className="h-5 w-5 text-primary-100" />
                  <h3 className="text-sm font-semibold uppercase tracking-[0.04em] text-[var(--color-text)]">{feature.title}</h3>
                  <p className="text-xs leading-5 text-[var(--color-text-muted)]">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
            © 2026 GymGamify. Powered by Polygon Media.
          </p>
        </div>
      </div>

      {/* Right side — login form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12" style={{ background: 'var(--color-surface)' }}>
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher variant="buttons" />
          </div>

          {/* Mobile logo */}
          <Link to="/" className="lg:hidden flex items-center gap-3 justify-center mb-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary-200/30 bg-primary-200/10 font-display text-lg uppercase text-primary-100">GG</span>
            <p className="font-display text-lg uppercase tracking-[0.18em] text-[var(--color-text)]">GymGamify</p>
          </Link>

          <div className="space-y-5">
            <div>
              <span className="hero-kicker">{t('loginPage.form.kicker', { defaultValue: 'Sign in' })}</span>
              <h2 className="mt-3 editorial-title text-3xl text-[var(--color-text)]">
                {t('loginPage.form.title', { defaultValue: 'Pick up where you left off.' })}
              </h2>
              <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                {t('loginPage.form.subtitle', { defaultValue: 'Enter your credentials to access your account.' })}
              </p>
            </div>

            {error && (
              <div className="border border-red-500/30 bg-red-500/10 text-red-200 p-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">
                  {t('loginPage.form.emailLabel', { defaultValue: 'Email address' })}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email"
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">
                  {t('loginPage.form.passwordLabel', { defaultValue: 'Password' })}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-4 py-3 pr-12 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                    placeholder="••••••••"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-300 text-surface font-semibold py-3.5 rounded-xl hover:from-primary-300 hover:to-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20"
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-surface border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {t('loginPage.form.submit', { defaultValue: 'Sign in' })}
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="pt-2 text-center">
              <Link to="/forgot-password" className="text-sm text-primary-100 hover:text-primary-200">
                {t('loginPage.form.forgot', { defaultValue: 'Forgot your password?' })}
              </Link>
            </div>

            <div className="pt-4 border-t border-[var(--color-border)]">
              <p className="text-center text-sm text-[var(--color-text-muted)]">
                {t('loginPage.form.noAccount', { defaultValue: "Don't have an account?" })}{' '}
                <Link to="/register" className="text-primary-100 font-semibold hover:text-primary-200">
                  {t('loginPage.form.createOne', { defaultValue: 'Create one' })}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
